<script setup lang="ts">
import sportUrlBuilder from "~/utils/sport/sportUrlBuilder";
import EventBreakdown from "./EventBreakdown.vue";
import EventScore from "./EventScore.vue";
import { useBetbook } from "~/store/betbook";
import { IEvent } from "~/types/sports/event";
import { useSettings } from "~/store/settings";
import TwoUpLogo from "~/components/ui/TwoUpLogo.vue";
import Tooltip from "~/components/ui/Tooltip.vue";
import { getJerserySrc, handleTeamJerseyError, hasLeagueJersey } from "~/utils/getJerseySrc";

const store = useBetbook();

interface Props {
	eventId: number;
	isTotalGoals?: boolean;
	showEventBreakdown?: boolean;
}

const settings = useSettings();
const route = useRoute();
const { fixtureType } = route.params;

const props = defineProps<Props>();
const event = computed(() => store.events.get(props.eventId));
const score = computed(() => store.scores.get(props.eventId));
const hasJersey = hasLeagueJersey(event.value?.leagueId, event.value?.regionId);
</script>

<template>
	<NuxtLink
		rel="ugc noindex nofollow"
		:href="
			sportUrlBuilder({
				fixtureType: fixtureType,
				sport: event?.sportId,
				event: {
					awayTeam: event?.awayTeam ?? '',
					homeTeam: event?.homeTeam ?? '',
					eventId: event?.eventId ?? 0,
					leagueId: event?.leagueId ?? '',
					regionId: event?.regionId ?? '',
					expectedEndEpoch: event?.expectedEndEpoch ?? 0,
					expectedStartEpoch: event?.expectedStartEpoch ?? 0,
				},
			})
		"
		class="flex flex-col items-center col-span-6 md:col-span-5 md:gap-1 md:items-center md:flex-row base-text overflow-hidden"
	>
		<div class="flex flex-row w-full gap-1 pr-2 md:w-40 shrink-0 md:pr-0 text-xs">
			<div
				class="flex flex-col justify-between"
				v-if="event?.homeTeam && event.awayTeam && settings.hasFeature('IsShowJerseysEnabled') && !settings.isDataFree"
			>
				<div class="w-4 h-4 overflow-hidden">
					<NuxtImg
						:key="event.homeTeam"
						:width="16"
						:draggable="false"
						:height="16"
						:src="getJerserySrc(true, event.eventId, hasJersey)"
						class="object-contain"
						@error="(e: Event) => handleTeamJerseyError(e, event?.homeTeam || '', event?.sportId || '')"
					/>
				</div>
				<div class="w-4 h-4 overflow-hidden">
					<NuxtImg
						:key="event.awayTeam"
						:width="16"
						:draggable="false"
						:height="16"
						:src="getJerserySrc(false, event.eventId, hasJersey)"
						class="object-contain"
						@error="(e: Event) => handleTeamJerseyError(e, event?.awayTeam || '', event?.sportId || '')"
					/>
				</div>
			</div>
			<div
				v-if="event?.homeTeam && event.awayTeam"
				class="flex flex-col justify-between overflow-hidden whitespace-nowrap gap-[2px] leading-5 md:leading-[16px]"
			>
				<strong class="overflow-hidden text-ellipsis">{{ event?.homeTeam }}</strong>
				<strong class="overflow-hidden text-ellipsis">
					{{ event?.awayTeam }}
				</strong>
			</div>
			<div v-else>
				<strong>{{ event?.displayName }}</strong>
			</div>
			<EventScore :score="score" v-if="event?.isLive" />
		</div>
		<div v-if="showEventBreakdown" class="flex w-full items-start md:items-center overflow-hidden justify-between pr-2">
			<div class="w-[1px] bg-light-300 dark:bg-dark-500 h-6 hidden md:flex mr-2" />
			<EventBreakdown :event="(event as IEvent)" :score="score" />
			<TwoUpLogo
				v-if="
					event?.isBookedForLive &&
					event.sportId === 'soccer' &&
					!event.isLive &&
					store.displayMarkets[0].name === '[Win/Draw/Win]' &&
					settings.hasFeature('Is2UpEnabled')
				"
			/>
		</div>
	</NuxtLink>
</template>
